import { useEffect, useState } from 'react';
import { 
  Sheet, 
  SheetContent, 
  SheetDescription, 
  SheetHeader, 
  SheetTitle 
} from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { RoleForm } from './RoleForm';
import { RolePermissionsEditor } from './RolePermissionsEditor';
import { RoleDeleteDialog } from './RoleDeleteDialog';
import { Role } from '@/types/roles';
import { useCreateRole, useUpdateRole, useRoleUserCount } from '@/hooks/roles';
import { Button } from '@/components/ui/button';
import { Trash2 } from 'lucide-react';

interface RoleDrawerProps {
  open: boolean;
  onClose: () => void;
  role: Role | null;
  allRoles: Role[];
}

export function RoleDrawer({ open, onClose, role, allRoles }: RoleDrawerProps) {
  const [activeTab, setActiveTab] = useState('details');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  
  const createRole = useCreateRole();
  const updateRole = useUpdateRole();
  const { data: userCount } = useRoleUserCount(role?.id || null);

  const isEditing = !!role;
  const canDelete = role && !role.is_system;

  useEffect(() => {
    if (open) {
      setActiveTab('details');
    }
  }, [open, role]);

  const handleSave = async (values: Partial<Role>) => {
    if (isEditing) {
      await updateRole.mutateAsync({ id: role.id, ...values });
    } else {
      await createRole.mutateAsync(values);
    }
    onClose();
  };

  const handleDelete = () => {
    setShowDeleteDialog(true);
  };

  const handleDeleteComplete = () => {
    setShowDeleteDialog(false);
    onClose();
  };

  return (
    <>
      <Sheet open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>
              {isEditing ? `Edit Role: ${role.name}` : 'Create New Role'}
            </SheetTitle>
            <SheetDescription>
              {isEditing 
                ? 'Modify role details and permissions' 
                : 'Define a new role with specific permissions'}
            </SheetDescription>
          </SheetHeader>

          <div className="mt-6">
            {isEditing ? (
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="details">Details</TabsTrigger>
                  <TabsTrigger value="permissions">Permissions</TabsTrigger>
                </TabsList>
                
                <TabsContent value="details" className="mt-4">
                  <RoleForm
                    role={role}
                    allRoles={allRoles}
                    onSave={handleSave}
                    onCancel={onClose}
                    isLoading={updateRole.isPending}
                  />
                  
                  {canDelete && (
                    <div className="mt-6 pt-6 border-t">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-destructive">
                            Danger Zone
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {(userCount ?? 0) > 0 
                              ? `${userCount} user${userCount === 1 ? '' : 's'} assigned to this role`
                              : 'No users assigned to this role'}
                          </p>
                        </div>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={handleDelete}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete Role
                        </Button>
                      </div>
                    </div>
                  )}
                </TabsContent>
                
                <TabsContent value="permissions" className="mt-4">
                  <RolePermissionsEditor roleId={role.id} />
                </TabsContent>
              </Tabs>
            ) : (
              <RoleForm
                role={null}
                allRoles={allRoles}
                onSave={handleSave}
                onCancel={onClose}
                isLoading={createRole.isPending}
              />
            )}
          </div>
        </SheetContent>
      </Sheet>

      {role && (
        <RoleDeleteDialog
          open={showDeleteDialog}
          onClose={() => setShowDeleteDialog(false)}
          role={role}
          allRoles={allRoles}
          userCount={userCount || 0}
          onDeleteComplete={handleDeleteComplete}
        />
      )}
    </>
  );
}
